import { useRef, useState } from 'react'
import { PULSE_COLORS } from './pulseConfig.js'

function toSafeFilename(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

function ExportPngButton({ exporting, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={exporting}
            title="Export as image"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                borderRadius: '6px',
                border: `1px solid ${PULSE_COLORS.border}`,
                background: exporting ? PULSE_COLORS.bgInner : 'transparent',
                color: PULSE_COLORS.textMuted,
                fontSize: '12px',
                fontWeight: 600,
                cursor: exporting ? 'not-allowed' : 'pointer',
                opacity: exporting ? 0.6 : 1,
            }}
        >
            {exporting ? 'Exporting...' : 'Export'}
        </button>
    )
}

async function exportNodeToPng(node, filename) {
    const rect = node.getBoundingClientRect()
    const width = Math.ceil(rect.width)
    const height = Math.ceil(rect.height)
    if (!width || !height) return

    const clone = node.cloneNode(true)
    clone.querySelectorAll('[data-export-ignore="true"]').forEach((el) => el.remove())
    clone.style.width = `${width}px`
    clone.style.minHeight = `${height}px`
    clone.style.boxSizing = 'border-box'

    const serialized = new XMLSerializer().serializeToString(clone)
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
  <foreignObject width="100%" height="100%">
    <div xmlns="http://www.w3.org/1999/xhtml" style="background:${PULSE_COLORS.bgInner};">${serialized}</div>
  </foreignObject>
</svg>`

    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }))
    try {
        await new Promise((resolve, reject) => {
            const image = new Image()
            image.onload = () => {
                const canvas = document.createElement('canvas')
                canvas.width = width * 2
                canvas.height = height * 2
                const ctx = canvas.getContext('2d')
                ctx.scale(2, 2)
                ctx.drawImage(image, 0, 0, width, height)
                const link = document.createElement('a')
                link.href = canvas.toDataURL('image/png')
                link.download = `${toSafeFilename(filename)}.png`
                link.click()
                resolve()
            }
            image.onerror = reject
            image.src = url
        })
    } finally {
        URL.revokeObjectURL(url)
    }
}

export default function ExportableSection({ children, filename }) {
    const sectionRef = useRef(null)
    const [exporting, setExporting] = useState(false)

    const handleExport = async () => {
        if (!sectionRef.current || exporting) return
        try {
            setExporting(true)
            await exportNodeToPng(sectionRef.current, filename)
        } catch (error) {
            console.warn('Pulse section export failed:', error)
        } finally {
            setExporting(false)
        }
    }

    if (typeof children === 'function') {
        return children({
            captureRef: sectionRef,
            exportButton: (
                <span data-export-ignore="true">
                    <ExportPngButton exporting={exporting} onClick={handleExport} />
                </span>
            ),
        })
    }

    return (
        <div ref={sectionRef} style={{ height: '100%', minWidth: 0 }}>
            {children}
        </div>
    )
}
